import React from 'react'
import CalendarPicker from 'components/calendar_picker'
import EventGroups from 'containers/purchase/modules/event_module/event_groups'
import {
  ModulePanel,
  ModuleHeader,
  ModuleBody,
  ModuleFooter
} from 'containers/purchase/modules/module_panel'
import { Row, Col, Alert } from 'reactstrap'
import time from 'i18n/time'
import { groupBy } from 'lodash'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import resCn from 'lib/responsive_classes'

const EventModule = (props) => {
  const { config, machine, status, resSize } = props
  const [current, send] = machine
  const { initialDate, locale, external_api_config } = config
  const { days_available, day, event, events, event_groups } = current.context
  const eventRenderMode = external_api_config.event_render_mode || 'time'

  return (
    <ModulePanel>
      <ModuleHeader
        status={status}
        onClick={() =>
          status === 'completed' ? send('REOPEN_EVENTSTEP') : null
        }
      >
        <EventSummary day={day} event={event} />
      </ModuleHeader>
      <ModuleBody status={status}>
        <Row>
          <Col
            resCn={resCn({
              wide: 'col-md-5 col-lg-4',
              slim: 'col-12',
              all: 'd-flex justify-content-center'
            }, resSize)}
          >
            <CalendarPicker
              initialDay={initialDate}
              onDaySelected={(day) => send({ type: 'DAY_SELECTION', day })}
              onMonthChange={(date) =>
                send({
                  type: 'MONTH_CHANGE',
                  year_month: moment(date).format('YYYY-MM')
                })
              }
              daysAvailable={days_available}
              stepStatus={status}
              locale={locale}
              resSize={resSize}
            />
          </Col>
          <Col
            className={resCn({
              wide: 'col-md-7 col-lg-8 mt-md-0',
              slim: 'mt-3',
              all: ''
            }, resSize)}
          >
            <EventsSection
              eventGroups={event_groups}
              eventRenderMode={eventRenderMode}
              isFetchingCalendar={current.matches({ eventStep: 'fetchingCalendar' })}
              isFetchingEvents={current.matches({ eventStep: 'fetchingEvents' })}
              send={send}
              event={events}
              day={day}
              resSize={resSize}
            />
          </Col>
        </Row>
      </ModuleBody>
      <ModuleFooter {...props} />
    </ModulePanel>
  )
}

const EventsSection = ({
  day,
  eventGroups,
  event,
  isFetchingEvents,
  isFetchingCalendar,
  eventRenderMode,
  send,
  resSize
}) => {
  const { i18n } = useTranslation()

  if (isFetchingCalendar) {
    return (
      <Alert color="light" className="d-flex align-items-center">
        <i className="fa fa-hourglass-half me-2" aria-hidden="true"></i>
        {i18n.t('event_module.loading_calendar')}...
      </Alert>
    )
  }

  if (isFetchingEvents) {
    return (
      <Alert color="light" className="d-flex align-items-center">
        <i className="fa fa-hourglass-half me-2" aria-hidden="true"></i>
        {i18n.t('event_module.loading_events')}...
      </Alert>
    )
  }

  if (!day) {
    return (
      <Alert color="info" className="d-flex align-items-center">
        <i className="far fa-calendar-alt me-2" aria-hidden="true"></i>
        {i18n.t('event_module.select_a_day')}
      </Alert>
    )
  }

  if (eventGroups.length > 0) {
    const selectionGroups = groupBy(
      eventGroups,
      (item) => item.selection_group && item.selection_group.id
    )

    if (Object.keys(selectionGroups).length > 1) {
      return Object.values(selectionGroups).map((selectionGroup, idx) => (
        <EventGroups
          eventRenderMode={eventRenderMode}
          key={idx}
          data={selectionGroup}
          eventsActive={event}
          onEventSelected={(event_item) =>
            send({ type: 'EVENT_SELECTION', event: event_item })
          }
          resSize={resSize}
        />
      ))
    }

    return (
      <EventGroups
        eventRenderMode={eventRenderMode}
        data={eventGroups}
        eventActive={event}
        resSize={resSize}
        onEventSelected={(event_item) =>
          send({ type: 'EVENT_SELECTION', event: event_item })
        }
      />
    )
  }

  return (
    <Alert color="light" className="text-center">
      {i18n.t('event_module.events_not_available')}
    </Alert>
  )
}

const EventSummary = ({ day, event }) => {
  const { i18n } = useTranslation()

  if (!day) {
    return (
      <>
        <div>
          <i className="far fa-calendar-alt me-2" aria-hidden="true"></i>
          {i18n.t('event_module.select_a_day')}
        </div>
      </>
    )
  }

  const dayLabel = time(day).format('LL')

  if (!event) {
    return (
      <>
        <div>
          <i className="far fa-calendar-alt me-2" aria-hidden="true"></i>
          <span>{dayLabel}</span>
        </div>
        <div>
          <span>{i18n.t('event_module.select_an_event')}</span>
        </div>
      </>
    )
  }

  return (
    <>
      <div>
        <i className="far fa-calendar-alt me-2" aria-hidden="true"></i>
        <span>{dayLabel}</span>
      </div>
      <div>
        <i className="far fa-clock me-2" aria-hidden="true"></i>
        <span>
          {event.description} {event.time}
        </span>
      </div>
    </>
  )
}

export default EventModule
